import React, { useEffect, useState } from "react";
import hrApi from "./hrApi";
import { Dashboard } from "./HrExtras";
import { Avatar, Skeleton } from "./HrUi";
import { ReadyCard } from "./Outbox";
import { Inbox, ChevronRight, UserMinus, UserPlus, FileCheck2, PartyPopper } from "lucide-react";

function Ring({ pct }) {
  const r = 42, c = 2 * Math.PI * r;
  return (
    <svg width="110" height="110" viewBox="0 0 110 110" data-testid="compliance-ring">
      <circle cx="55" cy="55" r={r} stroke="#e2e8f0" strokeWidth="11" fill="none" />
      <circle cx="55" cy="55" r={r} stroke={pct >= 80 ? "#22c55e" : pct >= 50 ? "#fb923c" : "#f43f5e"} strokeWidth="11" fill="none" strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} transform="rotate(-90 55 55)" style={{ transition: "stroke-dashoffset 900ms ease" }} />
      <text x="55" y="62" textAnchor="middle" fontSize="22" fontWeight="800" fill="#1e293b" fontFamily="Manrope, sans-serif">{`${pct}%`}</text>
    </svg>
  );
}

function TodayTile({ icon: Icon, label, value, people, tone, testid, onOpen }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
      <div className="flex items-center gap-2 text-sm text-slate-500"><span className={`w-8 h-8 rounded-xl grid place-items-center ${tone}`}><Icon size={16} /></span>{label}</div>
      <div className="text-3xl font-extrabold font-num text-slate-800 mt-2">{value}</div>
      {people?.length > 0 && <div className="flex -space-x-2 mt-2">{people.slice(0, 6).map((p) => <button key={p.id} title={p.name} onClick={() => onOpen(p.id)} className="ring-2 ring-white rounded-full"><Avatar name={p.name} size={26} /></button>)}</div>}
    </div>
  );
}

export function MyWeek({ onOpen, onGoto }) {
  const [o, setO] = useState(null);
  useEffect(() => { hrApi.get("/overview").then((r) => setO(r.data)); }, []);
  if (!o) return <Skeleton rows={5} />;
  const n = o.needs_confirmation;
  return (
    <div className="space-y-5" data-testid="my-week">
      <div><h1 className="font-heading font-extrabold text-3xl text-slate-800">My week</h1><p className="text-slate-500 text-sm">{new Date().toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}</p></div>
      <button data-testid="dash-needs-confirmation" onClick={() => onGoto("intake")}
        className={`w-full text-left rounded-2xl p-5 flex items-center gap-4 transition-all ${n ? "bg-gradient-to-r from-orange-500 to-rose-500 text-white shadow-lg shadow-orange-200 hover:scale-[1.01]" : "bg-white border border-slate-100 text-slate-700 shadow-sm"}`}>
        <span className={`w-12 h-12 rounded-2xl grid place-items-center ${n ? "bg-white/20" : "bg-emerald-50 text-emerald-600"}`}>{n ? <Inbox size={22} /> : <PartyPopper size={22} />}</span>
        <div className="flex-1"><div className="font-heading font-bold text-lg">{n ? `${n} item${n === 1 ? "" : "s"} need confirmation` : "Inbox zero"}</div>
          <div className="text-sm opacity-80">{n ? "Proposed action plans are waiting — nothing is filed until you confirm." : "No documents waiting. Drop files into Intake any time."}</div></div>
        <span className="text-3xl font-extrabold font-num" data-testid="dash-needs-confirmation-count">{n}</span><ChevronRight size={20} />
      </button>
      <ReadyCard count={o.ready_to_send || 0} onGoto={onGoto} />
      <div className="grid md:grid-cols-4 gap-4">
        <div className="md:col-span-1 bg-white rounded-2xl border border-slate-100 shadow-sm p-4 flex flex-col items-center" data-testid="compliance-health">
          <Ring pct={o.compliance_pct} />
          <div className="text-sm font-semibold text-slate-700 mt-1">Fully compliant</div>
          <div className="text-xs text-slate-400">{o.compliant} of {o.workforce} · contract + induction + key fields</div>
        </div>
        <TodayTile testid="today-absent" icon={UserMinus} label="Absent today" value={o.absent_today.length} people={o.absent_today} tone="bg-rose-50 text-rose-500" onOpen={onOpen} />
        <TodayTile testid="today-starters" icon={UserPlus} label="New starters this week" value={o.new_starters.length} people={o.new_starters} tone="bg-sky-50 text-sky-500" onOpen={onOpen} />
        <TodayTile testid="today-filed" icon={FileCheck2} label="Documents filed yesterday" value={o.filed_yesterday} tone="bg-emerald-50 text-emerald-600" onOpen={onOpen} />
      </div>
      <Dashboard onOpen={onOpen} onGoto={onGoto} />
    </div>
  );
}
